import { useEffect, useState, type ReactNode } from "react";
import { Alert, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useQuery } from "@tanstack/react-query";
import { errorMessage } from "@/api/client";

/**
 * A text field for a document identifier that **shows what the id actually points at**.
 *
 * Some ids can't be pickers: the operator arrives holding an accession number from a requisition,
 * a sample label, or a PACS worklist. Typing or pasting one is legitimate. The risk is not a
 * malformed id — that fails loudly — it is a *well-formed id for the wrong record*, which acts
 * silently on another patient.
 *
 * So the id is resolved as soon as it is entered and the record is echoed back underneath the
 * field. A wrong paste stops being invisible: the operator sees the wrong name before they act,
 * which is the only point at which they can still catch it.
 */
export function ResolvedIdField<T>({
  value, onChange, label, resolve, render, queryKey, required, disabled, placeholder, sx,
  onResolved,
}: {
  value: string;
  onChange: (id: string) => void;
  label: string;
  /** Fetch the record. Should reject when the id does not exist. */
  resolve: (id: string) => Promise<T>;
  /** Render the identity line(s) the operator checks against what they are holding. */
  render: (record: T) => ReactNode;
  /** Cache key prefix — keep distinct per record type. */
  queryKey: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  sx?: object;
  /** Fired with the resolved record (or null when it clears/fails). */
  onResolved?: (record: T | null) => void;
}) {
  const [input, setInput] = useState(value);
  const [committed, setCommitted] = useState(value);

  useEffect(() => { setInput(value); setCommitted(value); }, [value]);

  const q = useQuery({
    queryKey: [queryKey, committed],
    queryFn: () => resolve(committed.trim()),
    enabled: committed.trim().length > 0,
    // A mistyped id is expected input, not a transient failure — retrying just delays the message.
    retry: false,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!onResolved) return;
    onResolved(q.data ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data]);

  const commit = () => {
    const next = input.trim();
    setCommitted(next);
    onChange(next);
  };

  return (
    <Stack spacing={0.5} sx={sx}>
      <TextField
        size="small" fullWidth disabled={disabled}
        label={`${label}${required ? " *" : ""}`}
        placeholder={placeholder}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
        // Resolution failed — the id is well-formed enough to send but names nothing.
        error={!!q.isError}
        InputProps={{
          endAdornment: q.isFetching
            ? <CircularProgress size={16} />
            : q.data
              ? <CheckCircleIcon fontSize="small" color="success" />
              : undefined,
        }}
      />

      {q.isError && (
        <Alert severity="error" sx={{ py: 0, "& .MuiAlert-message": { py: 0.5 } }}>
          No {label.toLowerCase()} found for that id — check what you pasted. ({errorMessage(q.error)})
        </Alert>
      )}

      {q.data && (
        <Stack sx={{ pl: 1, borderLeft: 2, borderColor: "success.light" }}>
          {render(q.data)}
        </Stack>
      )}

      {!committed.trim() && !q.isFetching && (
        <Typography variant="caption" color="text.secondary">
          Paste or scan the id — the record it points at is shown here before you act on it.
        </Typography>
      )}
    </Stack>
  );
}
